import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';

type WebTabLayoutContextValue = {
  tabBarProps: BottomTabBarProps | null;
};

const WebTabLayoutContext = createContext<WebTabLayoutContextValue>({ tabBarProps: null });

function tabNavSignature(props: BottomTabBarProps): string {
  return `${props.state.key}:${props.state.index}:${props.state.routes.map((r) => r.key).join(',')}`;
}

/** Captures tab bar props for WebSidebar in the dashboard shell rail. */
export function WebTabBarBridge({ props }: { props: BottomTabBarProps }) {
  const { setTabBarProps } = useWebTabBarBridge();

  useLayoutEffect(() => {
    setTabBarProps(props);
  }, [props, setTabBarProps]);

  return null;
}

const WebTabBarBridgeContext = createContext<{
  setTabBarProps: (props: BottomTabBarProps) => void;
} | null>(null);

export function WebTabBarBridgeProvider({ children }: PropsWithChildren) {
  const [tabBarProps, setTabBarPropsState] = useState<BottomTabBarProps | null>(null);
  const signatureRef = useRef('');

  const setTabBarProps = useCallback((next: BottomTabBarProps) => {
    const nextSig = tabNavSignature(next);
    if (signatureRef.current === nextSig) {
      return;
    }
    signatureRef.current = nextSig;
    setTabBarPropsState(next);
  }, []);

  return (
    <WebTabBarBridgeContext.Provider value={{ setTabBarProps }}>
      <WebTabLayoutContext.Provider value={{ tabBarProps }}>{children}</WebTabLayoutContext.Provider>
    </WebTabBarBridgeContext.Provider>
  );
}

function useWebTabBarBridge() {
  const ctx = useContext(WebTabBarBridgeContext);
  if (!ctx) {
    throw new Error('WebTabBarBridge must be used within WebTabBarBridgeProvider');
  }
  return ctx;
}

export function useWebTabLayout() {
  return useContext(WebTabLayoutContext);
}
